import React, { useMemo, useState } from 'react';
import { Col, Form, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useRompslompLabels } from '../../../utils/rompslompLabels';

const MAX_RESULTS = 8;

// Searchable supplier dropdown: type to filter by name, legal name or alias; picking one links the expense to it.
export function SupplierPicker({ suppliers, value, onPick, onAdd, invalid, text, onText }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selected = suppliers.find((s) => s.id === value) || null;
    const q = (text || '').trim().toLowerCase();
    const matches = useMemo(() => suppliers.filter((s) => !q
        || `${s.name} ${s.legal_name || ''} ${(s.aliases || []).join(' ')}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS), [suppliers, q]);

    return (
        <div className="supplier-picker position-relative" data-testid="supplier-picker">
            <Form.Control type="text" autoComplete="off" role="combobox" aria-expanded={open} aria-label={t('expenses.accounting.supplier')}
                value={text} placeholder={t('expenses.supplierPlaceholder')} isInvalid={invalid}
                onFocus={() => setOpen(true)} onChange={(e) => { onText(e.target.value); setOpen(true); }} />
            {open && (
                <ListGroup className="supplier-picker-list position-absolute w-100 shadow-sm" role="listbox">
                    {matches.map((s) => (
                        <ListGroup.Item action as="button" type="button" key={s.id} role="option" aria-selected={s.id === value} active={s.id === value}
                            onClick={() => { onPick(s); setOpen(false); }}>
                            {s.name}{s.legal_name && s.legal_name !== s.name ? <span className="text-muted small"> · {s.legal_name}</span> : null}
                        </ListGroup.Item>
                    ))}
                    {matches.length === 0 && <ListGroup.Item disabled>{t('expenses.accounting.noSupplierFound')}</ListGroup.Item>}
                    <ListGroup.Item action as="button" type="button" variant="primary" onClick={() => { setOpen(false); onAdd((text || '').trim()); }} data-testid="add-supplier-inline">
                        + {t('expenses.accounting.addSupplier')}
                    </ListGroup.Item>
                </ListGroup>
            )}
            {selected && !open && <Form.Text className="text-muted">{t('expenses.accounting.linked', { name: selected.name })}</Form.Text>}
        </div>
    );
}

// Why an expense cannot be sent yet, as short plain text ("Missing: category, receipt"). Nothing for a sent expense.
const MISSING_KEYS = { needs_supplier: 'contact', needs_ledger: 'category', needs_treatment: 'treatment', needs_receipt: 'receipt', duplicate: 'duplicate' };

export function missingText(t, summary) {
    if (!summary || summary.status === 'sent') return '';
    if (summary.status === 'error') return (summary.reasons || [])[0] || '';
    const keys = [];
    if (!summary.supplier_id) keys.push('supplier');
    (summary.statuses || []).forEach((st) => { if (MISSING_KEYS[st] && !keys.includes(MISSING_KEYS[st])) keys.push(MISSING_KEYS[st]); });
    if (keys.length === 0) return '';
    return t('expenses.accounting.missing', { list: keys.map((k) => t(`expenses.accounting.missingItems.${k}`)).join(', ') });
}

// Short plain-text line under the supplier: wraps, never cut off.
export function MissingLine({ summary }) {
    const { t } = useTranslation();
    const text = missingText(t, summary);
    if (!text) return null;
    return <div className="expense-missing small text-muted" data-testid="expense-missing">{text}</div>;
}

export default function ExpenseAccountingFields({ options, acc, onChange, summary, supplierText, onSupplierText, onAddSupplier, supplierInvalid }) {
    const { t } = useTranslation();
    const labels = useRompslompLabels();
    const suppliers = options?.suppliers || [];
    const supplier = suppliers.find((s) => s.id === acc.supplier_id) || null;
    const costs = options?.cost_accounts || [];
    const treatments = options?.treatments || [];
    const defaultLedger = supplier && costs.find((a) => a.id === supplier.default_ledger_account_id);

    return (
        <div data-testid="expense-accounting-fields">
            <Row className="g-3">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>{t('expenses.accounting.supplier')}<span className="text-danger">*</span></Form.Label>
                        <SupplierPicker suppliers={suppliers} value={acc.supplier_id} text={supplierText} onText={onSupplierText}
                            onPick={(s) => onChange('supplier_id', s.id, s)} onAdd={onAddSupplier} invalid={supplierInvalid} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="expense-invoice-number">
                        <Form.Label>{t('expenses.accounting.invoiceNumber')}</Form.Label>
                        <Form.Control name="invoice_number" value={acc.invoice_number ?? ''} maxLength={100}
                            onChange={(e) => onChange('invoice_number', e.target.value)} placeholder={t('expenses.accounting.invoiceNumberPlaceholder')} />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="expense-category">
                        <Form.Label>{t('expenses.accounting.category')}</Form.Label>
                        <Form.Select name="ledger_account_id" value={acc.ledger_account_id ?? ''} onChange={(e) => onChange('ledger_account_id', e.target.value === '' ? '' : Number(e.target.value))}>
                            <option value="">{defaultLedger ? t('expenses.accounting.supplierDefault', { name: labels.account(defaultLedger) }) : t('expenses.accounting.noCategory')}</option>
                            {costs.map((a) => <option key={a.id} value={a.id}>{labels.account(a)}</option>)}
                        </Form.Select>
                        {costs.length === 0 && <Form.Text className="text-muted">{t('expenses.accounting.noCategories')}</Form.Text>}
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="expense-treatment">
                        <Form.Label>{t('expenses.accounting.treatment')}</Form.Label>
                        <Form.Select name="vat_treatment" value={acc.vat_treatment ?? ''} onChange={(e) => onChange('vat_treatment', e.target.value)}>
                            <option value="">{supplier?.default_vat_treatment ? t('expenses.accounting.supplierDefault', { name: t(`rompslomp.treatments.${supplier.default_vat_treatment}`) }) : t('expenses.accounting.noTreatment')}</option>
                            {treatments.map((k) => <option key={k} value={k}>{t(`rompslomp.treatments.${k}`)}</option>)}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <MissingLine summary={summary} />
                    {supplier && !supplier.rompslomp_contact_id && (
                        <div className="small text-muted">{t('expenses.accounting.noContactYet')}</div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

export const ACCOUNTING_FILTERS = ['all', 'needs_input'];

// Rows of the expenses list that match the accounting filter (All / Needs input).
export function matchesAccountingFilter(filter, summary) {
    if (filter === 'needs_input') return !summary || !!summary.needs_input;
    return true;
}
